import { useEffect, useMemo, useState } from "react";
import { message, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import styles from "./stylesDeclineRequestModal.module.css";

import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import { useWithdrawalReview } from "../WithdrawalReviewContext";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { handleWithdrawalReasonsConfig } from "../../../../utils/UserUtils";

function DeclineRequestModal() {
  const { t } = useTranslation();
  const { appConfig } = useSelector((state) => state.config);
  const withdrawalDeclinedReasonsConfig = appConfig?.withdrawal_declined_reasons;

  const [filterSegment, setFilterSegment] = useState(null);
  const [error, setError] = useState(false);
  const [touched, setTouched] = useState(false);

  const {
    isDeclineModalOpen,
    setIsDeclineModalOpen,
    handleDecline,
    recordDeclined,
    declineTransactionLoading,
  } = useWithdrawalReview();

  const defaultWithdrawalDeclinedReasons = useMemo(
    () => [
      { value: "Stolen card fraud", label: t("stolenCardFraud") },
      { value: "Friendly fraud", label: t("friendlyFraud") },
      { value: "ATO", label: t("ato") },
      { value: "Multi accounting", label: t("multiAccounting") },
      { value: "Account sharing", label: t("accountSharing") },
      { value: "Account selling", label: t("accountSelling") },
      { value: "Self Exclusion", label: t("selfExclusion") },
    ],
    [t],
  );

  const withdrawalDeclinedReasons =
    handleWithdrawalReasonsConfig(withdrawalDeclinedReasonsConfig) ||
    defaultWithdrawalDeclinedReasons;

  const reasonValid = Boolean(filterSegment);

  const handleClose = () => {
    setIsDeclineModalOpen(false);
  };

  const handleSave = async () => {
    const finalReason = filterSegment;
    if (!finalReason) {
      setError(true);
      setTouched(true);
      return;
    }
    
    try {
      message.info(AppStrings.REQUEST_BEING_PROCESSED);
      await handleDecline({ reason: finalReason, ...recordDeclined });
      message.success(AppStrings.WITHDRAWAL_DECLINED);
    } catch (err) {
      // notification handled in context
    }
  };

  useEffect(() => {
    setError(!reasonValid);
  }, [reasonValid]);

  useEffect(() => {
    if (isDeclineModalOpen) {
      setFilterSegment(null);
      setError(false);
      setTouched(false);
    }
  }, [isDeclineModalOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h2>{AppStrings.DECLINE_MODAL_TITLE}</h2>
        </div>
        <CloseOutlined onClick={handleClose} className={styles.closeIcon} />
      </div>

      <div className={styles.divider} />

      <div className={styles.content}>
        <div className={styles.reason}>
          <p>{AppStrings.REASON}</p>
        </div>
        <div className={styles.transactionItem}>
          <Select
            className={styles.selectField}
            value={filterSegment}
            onChange={(value) => setFilterSegment(value)}
            onBlur={() => setTouched(true)}
            options={withdrawalDeclinedReasons}
            placeholder={AppStrings.SELECT_A_REASON}
            allowClear
            virtual={false}
          />
        </div>
        {error && touched && (
          <div>
            <span className={styles.errorMessage}>
              {AppStrings.FIELD_IS_REQUIRED?.replace(
                "{title}",
                AppStrings.REASON,
              )}
            </span>
          </div>
        )}
      </div>
      
      <div className={styles.footer}>
        <div className={styles.buttons}>
          <SaveButton
            cancelButton
            cancelButtonText={AppStrings.CLOSE}
            callbackFunction={handleSave}
            callbackFunctionCancel={handleClose}
            isDisabled={error}
            isApiRunning={declineTransactionLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default DeclineRequestModal;
